// 계약 관리 — 상세(정보·이력·알림 기록(C4b) 탭 · 원본 열어 보기(PDF 만)/내려받기 · 편집 등급만 수정(C2b)·휴지통으로).
// 등급으로 단추를 숨길 뿐이고 막는 것은 서버다(require_contract_level).
// 알림 기록의 실패 사유는 시스템 관리자(can.settings)에게만 그린다 — 화면에서 가릴 뿐 응답 notices[].detail 은 그대로다.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { contractsAPI } from '../api/client';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';
import { COLORS, btn, card } from '../utils/uiStyles';
import { fmtKstDateTime } from '../utils/kstTime';
import ContractFormModal from './contracts/ContractFormModal';
import {
  CATEGORY_LABELS, FIELD_LABELS, PARTY_LABELS, REGION_LABELS, TERM_LABELS,
  daysText, dueText, errorText, eventLabel, eventValueText, fileSizeText, isUrgent, labelOf,
  noticeResultText, offsetText,
} from './contracts/contractLabels';

const EVENT_LIMIT = 200;   // 서버가 이력을 최근 200건까지만 준다
const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const rowHead = { ...td, width: '130px', color: COLORS.textSub, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' };
const text = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const whoWhen = (name, at) => [name, fmtKstDateTime(at)].filter(Boolean).join(' · ') || '—';

export default function ContractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [canSettings, setCanSettings] = useState(false);   // 시스템 관리자 — 알림 실패 사유를 본다
  const [tab, setTab] = useState('info');
  const [editForm, setEditForm] = useState(null);   // 열린 수정 창 { contract, departments, departmentsFailed }
  const [openingEdit, setOpeningEdit] = useState(false);   // 수정 창을 열려고 부서를 읽는 중
  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const openingEditRef = useRef(false);
  // 열어 보기로 만든 blob URL — 화면을 떠날 때 한꺼번에 지운다(60초 뒤 지우면 그 탭의 새로고침·저장이 깨진다).
  const blobUrlsRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    const blobUrls = blobUrlsRef.current;
    // 등급 읽기가 실패하면 열람(view)으로 본다 — 단추를 숨기는 쪽으로 틀린다.
    contractsAPI.me()
      .then((r) => {
        if (!mountedRef.current) return;
        setCanEdit(r.data?.can?.edit === true);
        setCanSettings(r.data?.can?.settings === true);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setCanEdit(false);
        setCanSettings(false);
      });
    return () => {
      mountedRef.current = false;
      requestSeqRef.current += 1;
      blobUrls.forEach((url) => { try { URL.revokeObjectURL(url); } catch { /* 지울 수 없으면 둔다 */ } });
      blobUrls.length = 0;
    };
  }, []);

  useEffect(() => {
    // 늦게 온 응답 버리기 — id 가 바뀌면 앞 id 의 응답은 번호가 달라 버려진다.
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    const isCurrent = () => mountedRef.current && requestSeqRef.current === seq;
    setContract(null);
    setNotFound(false);
    setError('');
    setTab('info');
    setEditForm(null);   // 옛 계약의 수정 창이 새 주소의 화면을 덮어쓰지 않게 닫는다
    contractsAPI.get(id)
      .then((r) => { if (isCurrent()) setContract(r.data || null); })
      .catch(async (err) => {
        if (err?.response?.status === 404) {
          if (isCurrent()) setNotFound(true);
          return;
        }
        // 숫자가 아닌 id 의 422 는 detail 이 객체 목록이라 대체 문구가 나온다.
        const message = await errorText(err, '계약을 불러오지 못했습니다.');
        if (isCurrent()) setError(message);
      });
  }, [id]);

  const goList = () => navigate('/contracts');
  const isPdf = String(contract?.file_ext || '').toLowerCase() === 'pdf';

  const openFile = async () => {
    if (!contract || !isPdf) return;
    // 팝업 차단을 피하려고 클릭 안에서 먼저 연다.
    const w = window.open('', '_blank');
    if (!w) {
      await alertDialog('새 탭이 막혔습니다. 브라우저의 팝업 허용을 확인해 주세요.');
      return;
    }
    try { w.opener = null; } catch { /* 열린 탭이 이 화면을 못 건드리게 — 막는 브라우저는 그대로 둔다 */ }
    try {
      const r = await contractsAPI.file(contract.id, 'inline');
      // 🔴 받은 type 을 믿지 않는다 — 언제나 application/pdf 로 고정한다.
      //    blob URL 탭에는 서버의 Content-Security-Policy: sandbox 가 걸리지 않는다.
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      // 받는 도중 화면을 떠났으면 정리(언마운트)가 이미 지나갔다 — 목록에 넣으면 영영 안 지워진다.
      if (!mountedRef.current) {
        try { URL.revokeObjectURL(url); } catch { /* 지울 수 없으면 둔다 */ }
        try { w.close(); } catch { /* 이미 닫혔으면 둔다 */ }
        return;
      }
      blobUrlsRef.current.push(url);
      w.location.href = url;
    } catch (err) {
      try { w.close(); } catch { /* 이미 닫혔으면 둔다 */ }
      await alertDialog(await errorText(err, '열지 못했습니다.'));
    }
  };

  const downloadFile = async () => {
    if (!contract) return;
    try {
      const r = await contractsAPI.file(contract.id, 'attachment');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = contract.file_name || 'contract';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* 지울 수 없으면 둔다 */ } }, 4000);
    } catch (err) {
      await alertDialog(await errorText(err, '내려받지 못했습니다.'));
    }
  };

  // 수정 창 — 부서 목록을 읽은 뒤 연다. 읽기가 실패해도 열고(현재 부서는 «(지금 값)») 수정은 막지 않는다.
  const openEdit = async () => {
    if (!contract || openingEditRef.current) return;
    openingEditRef.current = true;
    setOpeningEdit(true);
    const seq = requestSeqRef.current;   // 읽는 사이 id 가 바뀌거나 화면을 떠나면 번호가 달라진다
    const target = contract;
    let list = [];
    let failed = false;
    try {
      const r = await contractsAPI.departments();
      list = Array.isArray(r.data) ? r.data : [];
    } catch {
      failed = true;
    }
    openingEditRef.current = false;
    if (mountedRef.current) setOpeningEdit(false);   // 버려져도(id 가 바뀜) 단추는 «수정» 으로 돌아온다
    if (!mountedRef.current || requestSeqRef.current !== seq) return;
    setEditForm({ contract: target, departments: list, departmentsFailed: failed });
  };

  // 응답이 곧 새 상세다(이력 포함) — 다시 읽지 않고 그것으로 바꾼다 · 탭은 그대로.
  const onEdited = (detail) => {
    setEditForm(null);
    if (mountedRef.current && detail) setContract(detail);
  };

  const trashContract = async () => {
    if (!contract) return;
    if (!(await confirmDialog('이 계약을 휴지통으로 보낼까요? 휴지통에서 복원할 수 있습니다.', { danger: true }))) return;
    try {
      await contractsAPI.trash(contract.id);
      toast('휴지통으로 보냈습니다.');
      navigate('/contracts');
    } catch (err) {
      await alertDialog(await errorText(err, '휴지통으로 보내지 못했습니다.'));
    }
  };

  const renderBody = () => {
    if (notFound || error) {
      return (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>{notFound ? '계약을 찾을 수 없습니다(지워졌거나 휴지통에 있습니다).' : error}</span>
          <button type="button" onClick={goList} style={btn('secondary', 'sm')}>목록으로</button>
        </div>
      );
    }
    if (!contract) return <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>;

    const c = contract;
    const events = Array.isArray(c.events) ? c.events : [];
    const notices = Array.isArray(c.notices) ? c.notices : [];
    const infoRows = [
      ['contract_no', text(c.contract_no)],
      ['category', labelOf(CATEGORY_LABELS, c.category)],
      ['region', labelOf(REGION_LABELS, c.region)],
      ['party_type', labelOf(PARTY_LABELS, c.party_type)],
      ['term_type', labelOf(TERM_LABELS, c.term_type)],
      ['contract_name', text(c.contract_name)],
      ['counterparty', text(c.counterparty)],
      ['center', text(c.center)],
      ['department', text(c.department)],
      ['owner_name', text(c.owner_name)],
      ['sub_owner_name', text(c.sub_owner_name)],
      ['start_date', text(c.start_date)],
      ['end_date', text(c.end_date)],
      ['notice_date', text(c.notice_date)],
      ['auto_renewal', c.auto_renewal ? '예' : '아니오'],
    ].map(([key, value]) => [FIELD_LABELS[key], value, key]);
    const urgent = isUrgent(c.days_left);

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div data-testid="contract-no" style={{ fontSize: '12px', color: COLORS.textSub }}>{text(c.contract_no)}</div>
            <h2 style={{ margin: '2px 0 4px', fontSize: '20px' }}>{text(c.contract_name)}</h2>
            <div data-testid="contract-counterparty" style={{ fontSize: '13px', color: COLORS.textSub }}>{text(c.counterparty)}</div>
          </div>
          {/* 여는 중에도 disabled 를 쓰지 않는다 — 초점을 가진 단추를 끄면 초점이 빠져 창을 닫은 뒤 돌아올 자리가 없다.
              다시 누름은 openingEditRef 가 막는다. */}
          {canEdit && (
            <button type="button" onClick={openEdit} aria-disabled={openingEdit || undefined} aria-busy={openingEdit || undefined}
              style={{ ...btn('secondary', 'sm'), opacity: openingEdit ? 0.65 : 1, cursor: openingEdit ? 'wait' : 'pointer' }}>
              {openingEdit ? '여는 중…' : '수정'}
            </button>
          )}
          {canEdit && <button type="button" onClick={trashContract} style={btn('danger', 'sm')}>휴지통으로 보내기</button>}
          <button type="button" onClick={goList} style={btn('secondary', 'sm')}>목록으로</button>
        </div>

        <div role="tablist" aria-label="계약 상세" style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '12px' }}>
          {[['info', '정보'], ['history', '이력'], ['notices', '알림 기록']].map(([key, label]) => (
            <button key={key} type="button" role="tab" id={`contract-tab-${key}`} aria-selected={tab === key} aria-controls={`contract-panel-${key}`}
              onClick={() => setTab(key)}
              style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${tab === key ? COLORS.primary : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: tab === key ? 700 : 500, color: tab === key ? COLORS.primaryDark : COLORS.textSub }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'info' ? (
          <div role="tabpanel" id="contract-panel-info" aria-labelledby="contract-tab-info">
            <section style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: '12px' }}>
              <table aria-label="계약 정보" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {infoRows.map(([label, value, key]) => (
                    <tr key={key}><th scope="row" style={rowHead}>{label}</th><td style={td}>{value}</td></tr>
                  ))}
                  <tr><th scope="row" style={rowHead}>기준일</th><td style={td}>{dueText(c)}</td></tr>
                  <tr><th scope="row" style={rowHead}>남은 날</th><td style={{ ...td, color: urgent ? COLORS.dangerText : COLORS.text, fontWeight: urgent ? 700 : 400 }}>{daysText(c.days_left)}</td></tr>
                  <tr><th scope="row" style={rowHead}>{FIELD_LABELS.summary}</th><td style={{ ...td, whiteSpace: 'pre-wrap' }}>{text(c.summary)}</td></tr>
                  <tr><th scope="row" style={rowHead}>등록</th><td style={td}>{whoWhen(c.created_by_name, c.created_at)}</td></tr>
                  <tr><th scope="row" style={rowHead}>수정</th><td style={td}>{whoWhen(c.updated_by_name, c.updated_at)}</td></tr>
                </tbody>
              </table>
            </section>

            <section aria-label="원본 파일" style={{ ...card, padding: '12px 14px' }}>
              <div style={{ fontSize: '12px', color: COLORS.textSub, fontWeight: 600, marginBottom: '8px' }}>원본 파일</div>
              {c.file_name ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                  <span data-testid="contract-file-name">{c.file_name}</span>
                  {fileSizeText(c.file_size) && <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{fileSizeText(c.file_size)}</span>}
                  <button type="button" onClick={openFile} disabled={!isPdf} style={{ ...btn('secondary', 'sm'), opacity: isPdf ? 1 : 0.5, cursor: isPdf ? 'pointer' : 'not-allowed' }}>열어 보기</button>
                  <button type="button" onClick={downloadFile} style={btn('secondary', 'sm')}>내려받기</button>
                  {!isPdf && <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>이 형식은 내려받아 열어 주세요</span>}
                </div>
              ) : (
                <span style={{ color: COLORS.textMuted, fontSize: '13px' }}>원본 파일 없음</span>
              )}
            </section>
          </div>
        ) : tab === 'history' ? (
          <div role="tabpanel" id="contract-panel-history" aria-labelledby="contract-tab-history">
            <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {events.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>이력이 없습니다.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table aria-label="변경 이력" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                    <thead><tr>{['때', '한 사람', '한 일', '칸', '전 → 후'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {events.map((ev, index) => {
                        const hasChange = !(ev.old_value === null || ev.old_value === undefined || ev.old_value === '')
                          || !(ev.new_value === null || ev.new_value === undefined || ev.new_value === '');
                        return (
                          <tr key={ev.id ?? index} data-testid="contract-event">
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKstDateTime(ev.created_at) || '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{ev.actor_name || '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{eventLabel(ev.action)}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{FIELD_LABELS[ev.field] || ev.field || ''}</td>
                            <td style={td}>{hasChange ? `${eventValueText(ev.field, ev.old_value)} → ${eventValueText(ev.field, ev.new_value)}` : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {events.length >= EVENT_LIMIT && (
                <div style={{ padding: '8px 12px', fontSize: '12px', color: COLORS.textMuted }}>최근 200건만 보입니다</div>
              )}
            </section>
          </div>
        ) : (
          <div role="tabpanel" id="contract-panel-notices" aria-labelledby="contract-tab-notices">
            <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {notices.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>알림 기록이 없습니다.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table aria-label="알림 발송 기록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                    <thead><tr>{['때', '기준일', '지점', '결과'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {notices.map((n) => {
                        // 서버 notices 에는 id 가 없다 — (기준일, 지점)이 계약 안에서 유일하다(contract_notices 유니크).
                        const failed = typeof n.detail === 'string' && n.detail.startsWith('fail:');
                        return (
                          <tr key={`${n.due_date}-${n.offset_days}`} data-testid="contract-notice">
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKstDateTime(n.sent_at) || '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{text(n.due_date)}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{offsetText(n.offset_days)}</td>
                            <td style={{ ...td, color: failed ? COLORS.dangerText : COLORS.text, fontWeight: failed ? 600 : 400 }}>{noticeResultText(n, canSettings)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </>
    );
  };

  return (
    <AppShell app="contracts">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '20px 24px', maxWidth: '1100px' }}>
          {renderBody()}
        </div>
      </main>
      {editForm && (
        <ContractFormModal
          mode="edit"
          contract={editForm.contract}
          departments={editForm.departments}
          departmentsFailed={editForm.departmentsFailed}
          onClose={() => setEditForm(null)}
          onSaved={onEdited}
        />
      )}
    </AppShell>
  );
}
